import axios, { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import store from '../store';
import { refreshAuthToken } from '../store/slices/authSlice';
// API configuration
import { getBaseUrl, API_CONFIG } from '../config/api';
// Session management
import { isTokenExpiringSoon, getToken, getCsrfToken } from '../utils/sessionManager';
import { coordinateAuthRefresh } from './authRefreshCoordinator';

interface QueryParams {
  [key: string]: any;
}

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: getBaseUrl(),
  timeout: API_CONFIG.TIMEOUT_MS,
  headers: {
    ...API_CONFIG.DEFAULT_HEADERS,
    'X-Client-Platform': 'web',
  },
  withCredentials: true,
});

// Add custom property to request config
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

/**
 * Refresh-token rotation is single-use on the API. All callers must share one
 * in-flight refresh so concurrent requests cannot consume the same cookie.
 */
export const refreshAccessTokenOnce = (): Promise<string> => {
  return coordinateAuthRefresh(() =>
    store.dispatch(refreshAuthToken()).unwrap()
      .then(({ token }) => {
        const refreshedToken = token || getToken();
        if (!refreshedToken) {
          throw new Error('Token refresh completed without an access token');
        }
        return refreshedToken;
      }),
  );
};

// Request interceptor: attach auth token and proactively refresh if expiring soon
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const state = store.getState();
    const token = state.auth.token || getToken();

    // CSRF for cookie-authenticated auth routes
    const csrf = getCsrfToken();
    if (csrf && config.headers && config.url?.includes('/auth/')) {
      config.headers['X-CSRF-Token'] = csrf;
    }
    
    // Proactive token refresh: If token is expiring soon (within 5 minutes), refresh it
    if (token && isTokenExpiringSoon() && !config.url?.includes('/auth/refresh')) {
      try {
        console.log('[SessionManager] Token expiring soon, refreshing proactively');
        const newToken = await refreshAccessTokenOnce();
        if (newToken && config.headers) {
          config.headers['Authorization'] = `Bearer ${newToken}`;
        }
        console.log('[SessionManager] Proactive token refresh successful');
        return config;
      } catch (error) {
        console.error('[SessionManager] Proactive token refresh failed:', error);
      }
    }
    
    if (token && config.headers) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor: refresh on 401; refresh thunk owns auth cleanup
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    
    // Skip interceptor logic for logout endpoint to prevent infinite loops
    if (originalRequest.url?.includes('/auth/logout')) {
      const errResp = {
        status: error.response?.status,
        message: (error.response?.data as any)?.message || 'An error occurred',
        data: error.response?.data,
      };
      return Promise.reject(errResp);
    }

    // Password reauth failures must not trigger session refresh / logout
    const responseData = error.response?.data as any;
    const isPasswordReauthFailure =
      responseData?.code === 'REAUTH_REQUIRED' ||
      (error.response?.status === 403 &&
        (originalRequest.url?.includes('/account/export') ||
          originalRequest.url?.includes('/account/delete-request') ||
          originalRequest.url?.includes('/account/delete')));

    if (isPasswordReauthFailure) {
      const errResp = {
        status: error.response?.status,
        message: responseData?.message || 'Password confirmation failed',
        code: responseData?.code || 'REAUTH_REQUIRED',
        data: responseData,
      };
      return Promise.reject(errResp);
    }
    
    // Handle network errors without a response (timeout, connection refused, etc.)
    if (!error.response && error.request) {
      const isTimeout = error.code === 'ECONNABORTED';
      const connectionError = {
        status: 0,
        message: isTimeout
          ? 'The request timed out. Large uploads may take longer—please try again.'
          : 'Connection refused. Please ensure the backend server is running.',
        code: isTimeout ? 'ECONNABORTED' : 'ERR_CONNECTION_REFUSED',
        data: null,
      };
      return Promise.reject(connectionError);
    }
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      console.log('[SessionManager] Received 401, attempting cookie-based token refresh');

      // Never recurse on the refresh endpoint itself
      if (originalRequest.url?.includes('/auth/refresh')) {
        const errResp = {
          status: error.response?.status,
          message: (error.response?.data as any)?.message || 'An error occurred',
          data: error.response?.data,
        };
        return Promise.reject(errResp);
      }

      try {
        const requestAuthorization = String(originalRequest.headers?.Authorization || '');
        const currentToken = store.getState().auth.token || getToken();

        // Another request may already have refreshed before this 401 was
        // handled. Retry with that token instead of rotating the cookie again.
        if (currentToken && requestAuthorization !== `Bearer ${currentToken}`) {
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${currentToken}`;
          }
          return apiClient(originalRequest);
        }

        // Cookie-based refresh — do not require a JS-held refresh token
        const newToken = await refreshAccessTokenOnce();
        if (newToken && originalRequest.headers) {
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          console.log('[SessionManager] Token refreshed successfully, retrying request');
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        console.error('[SessionManager] Token refresh failed:', refreshError);
      }

      // refreshAuthToken owns local cleanup. It preserves a newer CSRF value
      // if another tab won a simultaneous rotation.
    }
    if (error.response?.status === 429 && originalRequest.url?.includes('/notifications')) {
      // handle notifications backoff if desired
    }
    // Standardize error
    const errResp = {
      status: error.response?.status,
      message: (error.response?.data as any)?.message || 'An error occurred',
      data: error.response?.data,
    };
    return Promise.reject(errResp);
  }
);

// Create API client with more functionality to match mobile app behavior
const api = {
  // Direct access methods that match mobile's api implementation
  axios: apiClient,
  
  // Authentication
  auth: {
    login: (data: { email: string; password: string; turnstileToken: string }) =>
      apiClient.post('/auth/web/login', data),
    register: (data: any) => apiClient.post('/auth/web/register', data),
    refreshToken: (refreshToken: string) => apiClient.post(API_CONFIG.REFRESH_ENDPOINT, { refreshToken }),
    getCurrentUser: () => apiClient.get('/auth/me'),
    logout: () => apiClient.post('/auth/logout'),
  },

  // Profile
  user: {
    getProfile: () => apiClient.get('/profile/me'),
    updateProfile: (data: any) => apiClient.put('/profile/update', data),
    uploadProfilePhoto: (form: FormData) =>
      apiClient.post('/profile/photo', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
    deleteProfilePhoto: () => apiClient.delete('/profile/photo'),
  },

  // Notifications
  notification: {
    getNotifications: (params?: QueryParams) => apiClient.get('/notifications', { params }),
    markAsRead: (ids: string[]) => apiClient.put('/notifications/read', { notificationIds: ids }),
  },

  // Stylists
  stylist: {
    search: (params: any) => apiClient.get('/profile/stylists', { params }),
    getById: (id: string) => apiClient.get(`/profile/stylists/${id}`),
    getNearby: (lat: number, lng: number, radius?: number) => apiClient.get('/profile/stylists', { params: { latitude: lat, longitude: lng, radius } }),
    getServiceTemplates: (category?: string) =>
      category ? apiClient.get(`/service-templates/categories/${category}`) : apiClient.get('/service-templates'),
    getServices: (stylistId: string) => apiClient.get(`/stylist-services/stylist/${stylistId}`),
    createService: (stylistId: string, data: any) => apiClient.post('/stylist-services', { ...data, stylist: stylistId }),
    updateService: (serviceId: string, data: any) => apiClient.put(`/stylist-services/${serviceId}`, data),
    deleteService: (serviceId: string) => apiClient.delete(`/stylist-services/${serviceId}`),
    toggleServiceActive: (serviceId: string, isActive: boolean) => apiClient.patch(`/stylist-services/${serviceId}/active`, { isActive }),
    getServiceById: (serviceId: string) => apiClient.get(`/stylist-services/${serviceId}`),
  },

  // Appointments
  appointment: {
    create: (data: any) => apiClient.post('/appointments', data),
    getAll: (config?: { params?: QueryParams }) => apiClient.get('/appointments', config),
    getById: (id: string) => apiClient.get(`/appointments/${id}`),
    updateStatus: (id: string, data: any) => apiClient.put(`/appointments/${id}`, data),
    cancelAppointment: (id: string) => apiClient.put(`/appointments/${id}/cancel`),
  },

  // Portfolio
  portfolio: {
    getMyPortfolio: () => apiClient.get('/portfolio/me'),
    getPortfolio: (stylistId: string) => apiClient.get(`/portfolio/${stylistId}`),
    deletePortfolioItem: (id: string) => apiClient.delete(`/portfolio/${id}`),
    uploadPortfolioImage: (form: FormData) => apiClient.post('/portfolio/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
    uploadPortfolioVideo: (form: FormData) => apiClient.post('/portfolio/upload-video', form, { headers: { 'Content-Type': 'multipart/form-data' } }),
    updatePortfolioImage: (id: string, data: any) => apiClient.put(`/portfolio/${id}`, data),
  },

  chat: {
    uploadImage: (form: FormData) =>
      apiClient.post('/messages/media/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      }),
    uploadVideo: (form: FormData) =>
      apiClient.post('/messages/media/upload-video', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      }),
  },

  // Payments
  payment: {
    getPaymentMethods: () => apiClient.get('/payments/methods'),
    addPaymentMethod: (data: any) => apiClient.post('/payments/methods', data),
    deletePaymentMethod: (id: string) => apiClient.delete(`/payments/methods/${id}`),
    setDefaultPaymentMethod: (id: string) => apiClient.put(`/payments/methods/${id}/default`),
    getDefaultPaymentMethod: (type: string) => apiClient.get('/payments/methods/default', { params: { type } }),
    verifyBankAccount: (id: string, amounts: any) => apiClient.post(`/payments/methods/${id}/verify`, amounts),
  },

  // Generic
  get: (url: string, config?: { params?: QueryParams }) => apiClient.get(url, config),
  post: (url: string, data: any) => apiClient.post(url, data),
  put: (url: string, data: any) => apiClient.put(url, data),
  delete: (url: string) => apiClient.delete(url),
  patch: (url: string, data: any) => apiClient.patch(url, data),

  // Type‑safe
  getWithParams: <T = any>(url: string, params?: QueryParams) => apiClient.get<T>(url, { params }),
  postWithData: <T = any>(url: string, data: any) => apiClient.post<T>(url, data),
  putWithData: <T = any>(url: string, data: any) => apiClient.put<T>(url, data),
  patchWithData: <T = any>(url: string, data: any) => apiClient.patch<T>(url, data),
  deleteWithParams: <T = any>(url: string, params?: QueryParams) => apiClient.delete<T>(url, { params }),
};

export { apiClient };
export default api;
